import { FunctionComponent, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { InputField } from "@/components/InputField/InputField";
import { useSnackBar } from "@/utils/useSnackbar";

import { useLazyGetDynamicsOAuthConnectUrlQuery } from "@/pages/Private/redux/dynamics/dynamics.api";

import {
	createDynamicsConnectionSchema,
	DynamicsConnectionFormValues,
	MsdynamicsOAuthStatusResponse,
} from "../schema/dynamics";

type Ts = (key: string) => string;

interface DynamicsConnectionTabProps {
	oauthStatus?: MsdynamicsOAuthStatusResponse;
	isDisconnecting: boolean;
	onOpenFieldMapping: () => void;
	onRequestDisconnect: () => void;
	ts: Ts;
}

const getBackendMessage = (error: unknown): string | undefined => {
	if (typeof error === "object" && error !== null && "data" in error) {
		const data = (error as { data?: { message?: string | string[] } }).data;

		if (Array.isArray(data?.message)) {
			return data.message[0];
		}

		if (typeof data?.message === "string") {
			return data.message;
		}
	}

	return undefined;
};

/**
 * Connection tab of the Dynamics 365 settings.
 *
 * Unlike every other provider the connect action is **not** a bare link: a Dataverse access
 * token is scoped to exactly one environment, so `GET oauth/connect` requires the customer's
 * environment URL. The same rule the backend enforces before signing the state token is
 * mirrored in Zod here, so a typo is caught in the field rather than after a round-trip —
 * and when the backend still rejects it, its own message is surfaced verbatim.
 */
export const DynamicsConnectionTab: FunctionComponent<DynamicsConnectionTabProps> = ({
	oauthStatus,
	isDisconnecting,
	onOpenFieldMapping,
	onRequestDisconnect,
	ts,
}) => {
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const [getOAuthConnectUrl, { isLoading: isLoadingOAuthUrl }] =
		useLazyGetDynamicsOAuthConnectUrlQuery();

	const schema = useMemo(
		() => createDynamicsConnectionSchema(ts("oauth.environmentUrlInvalid")),
		[ts]
	);

	const {
		watch,
		setValue,
		handleSubmit,
		formState: { errors, isValid },
	} = useForm<DynamicsConnectionFormValues>({
		resolver: zodResolver(schema),
		mode: "onChange",
		defaultValues: { environmentUrl: "" },
	});

	const environmentUrl = watch("environmentUrl");

	const isConnected = oauthStatus?.connected ?? false;

	const handleConnect = handleSubmit(async ({ environmentUrl }) => {
		try {
			const result = await getOAuthConnectUrl({ environmentUrl }).unwrap();

			window.location.href = result.url;
		} catch (error) {
			handleOpenSnackbar(getBackendMessage(error) ?? ts("oauth.connectError"));
		}
	});

	if (isConnected) {
		return (
			<div className="flex flex-col gap-4">
				{renderMessageSnackbar()}
				<div className="flex items-center gap-3 p-4 rounded-lg border border-status-success bg-green-50">
					<Icon className="w-5 h-5 text-status-success" icon={faCircleCheck} />
					<div className="flex flex-col">
						<span className="text-sm font-semibold text-status-success">
							{ts("oauth.connectedViaOAuth")}
						</span>
						{oauthStatus?.apiDomain && (
							<span className="text-xs text-text-meta" data-testid="dynamics-environment-url">
								{ts("oauth.environmentUrl")}: {oauthStatus.apiDomain}
							</span>
						)}
						{oauthStatus?.tenantId && (
							<span className="text-xs text-text-meta">
								{ts("oauth.tenantId")}: {oauthStatus.tenantId}
							</span>
						)}
						{oauthStatus?.msdynamicsUserId && (
							<span className="text-xs text-text-meta">
								{ts("oauth.msdynamicsUserId")}: {oauthStatus.msdynamicsUserId}
							</span>
						)}
						<span className="text-xs text-text-meta">{ts("oauth.tokenRefresh")}</span>
					</div>
				</div>
				<div className="flex flex-row gap-2">
					<div className="flex flex-wrap gap-2">
						<Button
							color={ButtonColor.DANGER_SECONDARY}
							isLoading={isDisconnecting}
							size={ButtonSize.S}
							title={ts("disconnect")}
							onClick={onRequestDisconnect}
						/>
					</div>
					<div className="w-fit">
						<Button
							color={ButtonColor.ACTION}
							size={ButtonSize.S}
							title={ts("configureFieldMapping")}
							onClick={onOpenFieldMapping}
						/>
					</div>
				</div>
			</div>
		);
	}

	return (
		<form className="flex flex-col gap-3" onSubmit={handleConnect}>
			{renderMessageSnackbar()}
			<div className="text-sm text-text-meta">{ts("oauth.environmentUrlDescription")}</div>
			<div className="w-full max-w-[420px]">
				<InputField
					error={errors.environmentUrl?.message}
					handleChange={(event) =>
						setValue("environmentUrl", event.target.value, {
							shouldValidate: true,
							shouldDirty: true,
						})
					}
					information={ts("oauth.environmentUrlHelp")}
					label={ts("oauth.environmentUrl")}
					name="environmentUrl"
					placeholder="https://org1234.crm4.dynamics.com"
					showError={Boolean(errors.environmentUrl)}
					testId="dynamics-environment-url-input"
					touched={Boolean(errors.environmentUrl)}
					value={environmentUrl}
				/>
				{errors.environmentUrl?.message && (
					<span className="mt-1 block text-xs text-status-error" role="alert">
						{errors.environmentUrl.message}
					</span>
				)}
			</div>
			<div className="max-w-[250px]">
				<Button
					color={ButtonColor.ACTION}
					disabled={!isValid}
					isLoading={isLoadingOAuthUrl}
					size={ButtonSize.S}
					testId="dynamics-connect-button"
					title={ts("oauth.connectWithDynamics")}
					type="submit"
				/>
			</div>
		</form>
	);
};
